import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster, toast } from 'sonner';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import AddProject from './pages/AddProject';
import EditProject from './pages/EditProject';
import Tasks from './pages/Tasks';
import TimeTracking from './pages/TimeTracking';
import Team from './pages/Team';
import TeamManagement from './pages/TeamManagement';
import AddTeamMember from './pages/AddTeamMember';
import EditTeamMember from './pages/EditTeamMember';
import TeamMemberDetails from './pages/TeamMemberDetails';
import Resources from './pages/Resources';
import ResourceProfile from './pages/ResourceProfile';
import GanttChart from './pages/GanttChart';

import Clients from './pages/Clients';
import AddClient from './pages/AddClient';
import EditClient from './pages/EditClient';
import ClientDetails from './pages/ClientDetails';
import Settings from './pages/Settings';
import HRAttendanceNew from './pages/HRAttendanceNew';
import OperationsHeadAttendance from './pages/OperationsHeadAttendance';
import ProjectManagerAttendance from './pages/ProjectManagerAttendance';
import ProjectLeadAttendance from './pages/ProjectLeadAttendance';
import DeveloperAttendance from './pages/DeveloperAttendance';
import Reports from './pages/Reports';
import Daysheet from './pages/Daysheet';
import DailyWorkSummary from './pages/DailyWorkSummary';
import DaysheetDetails from './pages/DaysheetDetails';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const PrivateRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors expand={false} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
        <Route path="/dashboard" element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } />
        <Route path="/projects" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager', 'Project Lead', 'Developer', 'QA Engineer', 'UI Engineer']}>
            <Projects />
          </ProtectedRoute>
        } />
        <Route path="/projects/add" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <AddProject />
          </ProtectedRoute>
        } />
        <Route path="/projects/edit/:id" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <EditProject />
          </ProtectedRoute>
        } />
        <Route path="/tasks" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager', 'Project Lead', 'Developer', 'QA Engineer', 'UI Engineer']}>
            <Tasks />
          </ProtectedRoute>
        } />
        <Route path="/time" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager', 'Project Lead', 'Developer', 'QA Engineer', 'UI Engineer']}>
            <TimeTracking />
          </ProtectedRoute>
        } />
        <Route path="/team" element={
          <PrivateRoute>
            <Team />
          </PrivateRoute>
        } />
        <Route path="/team-management" element={
          <ProtectedRoute allowedRoles={['Project Manager', 'Operations Head']}>
            <TeamManagement />
          </ProtectedRoute>
        } />
        <Route path="/team/add" element={
          <ProtectedRoute allowedRoles={['HR', 'Operations Head']}>
            <AddTeamMember />
          </ProtectedRoute>
        } />
        <Route path="/team/edit/:id" element={
          <ProtectedRoute allowedRoles={['HR', 'Operations Head']}>
            <EditTeamMember />
          </ProtectedRoute>
        } />
        <Route path="/team/view/:id" element={
          <PrivateRoute>
            <TeamMemberDetails />
          </PrivateRoute>
        } />
        <Route path="/resources" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <Resources />
          </ProtectedRoute>
        } />
        <Route path="/resources/:id" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <ResourceProfile />
          </ProtectedRoute>
        } />
        <Route path="/gantt" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager', 'Project Lead']}>
            <GanttChart />
          </ProtectedRoute>
        } />

        <Route path="/clients" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <Clients />
          </ProtectedRoute>
        } />
        <Route path="/clients/add" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <AddClient />
          </ProtectedRoute>
        } />
        <Route path="/clients/edit/:id" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <EditClient />
          </ProtectedRoute>
        } />
        <Route path="/clients/:id" element={
          <ProtectedRoute allowedRoles={['Operations Head', 'Project Manager']}>
            <ClientDetails />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        } />
        <Route path="/hr/attendance" element={
          <ProtectedRoute allowedRoles={['HR']}>
            <HRAttendanceNew />
          </ProtectedRoute>
        } />
        <Route path="/ops/attendance" element={
          <ProtectedRoute allowedRoles={['Operations Head']}>
            <OperationsHeadAttendance />
          </ProtectedRoute>
        } />
        <Route path="/pm/attendance" element={
          <ProtectedRoute allowedRoles={['Project Manager']}>
            <ProjectManagerAttendance />
          </ProtectedRoute>
        } />
        <Route path="/lead/attendance" element={
          <ProtectedRoute allowedRoles={['Project Lead']}>
            <ProjectLeadAttendance />
          </ProtectedRoute>
        } />
        <Route path="/developer/attendance" element={
          <ProtectedRoute allowedRoles={['Developer', 'QA Engineer', 'UI Engineer']}>
            <DeveloperAttendance />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={['HR', 'Operations Head']}>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/daysheet" element={
          <ProtectedRoute allowedRoles={['Developer', 'QA Engineer', 'UI Engineer', 'Project Lead', 'Project Manager', 'HR', 'Operations Head']}>
            <Daysheet />
          </ProtectedRoute>
        } />
        <Route path="/daysheet/today" element={
          <ProtectedRoute allowedRoles={['Developer', 'QA Engineer', 'UI Engineer', 'Project Lead', 'Project Manager', 'Operations Head', 'HR']}>
            <DailyWorkSummary />
          </ProtectedRoute>
        } />
        <Route path="/daysheet/:id" element={
          <ProtectedRoute allowedRoles={['Developer', 'QA Engineer', 'UI Engineer', 'Project Lead', 'Project Manager', 'Operations Head', 'HR']}>
            <DaysheetDetails />
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
